"use client";

import { CurvedRow } from "@/components/feature/curved-row";
import { GameRow } from "@/components/feature/game-row";
import { ShowcaseGrid } from "@/components/feature/showcase-grid";
import { type ContentOrderGameRow, organizeContentOrders } from "@/lib/content-orders";
import {
  readGameSectionCenterWhenSparse,
  readGameSectionCurveDirection,
  readGameSectionEnlargeMiddle,
  readGameSectionLayout,
  readGameSectionShowHeader,
  readGameSectionSidePadding,
  sectionToShowcaseItems,
} from "@/lib/game-sections";
import type { GameRow as GameRowType } from "@/lib/games";
import type { PageOriginContext } from "@/lib/page-origin-context";
import { isMobileDevice } from "@/lib/utils/device-detection";
import type { ContentOrderWithGames } from "@/types/content-order";
import type { ShowcaseGridDisplayItem } from "@/types/showcase-grid";
import { useMemo } from "react";

interface GamesRowsProps {
  contentOrders?: ContentOrderWithGames[];
  emptyMessage?: string;
  emptyDescription?: string;
  pageOrigin?: PageOriginContext | null;
}

type RenderedSection = GameRowType & {
  _orderId?: string;
  layout: "row" | "grid" | "curved_row";
  showHeader: boolean;
  centerWhenSparse: boolean;
  sidePadding: number;
  curveDirection: "up" | "down";
  enlargeMiddle: boolean;
  showcaseItems: ShowcaseGridDisplayItem[];
};

function GameSectionView({
  row,
  emptyMessage,
  emptyDescription,
  pageOrigin,
}: {
  row: RenderedSection;
  emptyMessage: string;
  emptyDescription: string;
  pageOrigin?: PageOriginContext | null;
}) {
  const rowKey = row._orderId ? `content-order-${row._orderId}` : row.tag.id;
  const previewRowScope = row._orderId ?? `tag-${row.tag.id}`;
  if (row.layout === "grid") {
    return (
      <section key={rowKey} data-testid="game-section-grid">
        {row.showHeader ? (
          <h3 className="mb-3 px-1 text-xl font-bold text-white">{row.tag.name}</h3>
        ) : null}
        <ShowcaseGrid
          items={row.showcaseItems}
          emptyMessage={emptyMessage}
          emptyDescription={emptyDescription}
          pageOrigin={pageOrigin}
          linkPrefetch={false}
          centerWhenSparse={row.centerWhenSparse}
          sidePadding={row.sidePadding}
        />
      </section>
    );
  }
  if (row.layout === "curved_row") {
    return (
      <section key={rowKey} data-testid="game-section-curved">
        {row.showHeader ? (
          <h3 className="mb-3 px-1 text-xl font-bold text-white">{row.tag.name}</h3>
        ) : null}
        <CurvedRow
          items={row.showcaseItems}
          direction={row.curveDirection}
          enlargeMiddle={row.enlargeMiddle}
          sidePadding={row.sidePadding}
          pageOrigin={pageOrigin}
        />
      </section>
    );
  }
  return (
    <div key={rowKey} data-testid="game-section-row">
      {row.showcaseItems.length > 0 ? (
        <GameRow
          tag={row.tag}
          items={row.showcaseItems}
          previewRowScope={previewRowScope}
          pageOrigin={pageOrigin}
          showHeader={row.showHeader}
        />
      ) : row.showHeader ? (
        <h3 className="px-1 text-xl font-bold text-white">{row.tag.name}</h3>
      ) : null}
    </div>
  );
}

export function GamesRows({
  contentOrders,
  emptyMessage = "No Rows Available",
  emptyDescription = "No games are organized in rows yet.",
  pageOrigin,
}: GamesRowsProps) {
  const isMobile = useMemo(() => isMobileDevice(), []);

  const gameRows = useMemo(() => {
    if (contentOrders && contentOrders.length > 0) {
      const organized = organizeContentOrders(contentOrders);
      return organized
        .map((orderRow: ContentOrderGameRow, index: number) => {
          let rowId: number;
          const orderId = orderRow.order.id;
          if (orderId.startsWith("temp-")) {
            rowId = -(index + 1);
          } else {
            const uuid = orderId.replace(/-/g, "");
            const firstPart = Number.parseInt(uuid.slice(0, 8), 16);
            const lastPart = Number.parseInt(uuid.slice(-8), 16);
            rowId = (firstPart ^ lastPart) + 2000000;
          }

          const rowGames = orderRow.games.filter((g) => (isMobile ? !g.desktop_only : true));
          const elements = orderRow.order.elements?.filter((element) => {
            if (element.content_type !== "game" || !element.game) return true;
            return isMobile ? !element.game.desktop_only : true;
          });
          const showcaseItems = sectionToShowcaseItems({
            id: orderRow.order.id,
            name: orderRow.order.name,
            description: orderRow.order.description,
            display_order: orderRow.order.display_order,
            active: true,
            created_at: "",
            updated_at: "",
            layout: orderRow.order.layout,
            show_header: orderRow.order.show_header,
            center_when_sparse: orderRow.order.center_when_sparse,
            side_padding: orderRow.order.side_padding,
            curve_direction: orderRow.order.curve_direction,
            enlarge_middle: orderRow.order.enlarge_middle,
            grid_slots: orderRow.order.grid_slots,
            elements,
            games: rowGames.map((game, gameIndex) => ({
              id: gameIndex,
              content_order_id: orderRow.order.id,
              game_id: game.id,
              display_order: gameIndex,
              created_at: "",
              updated_at: "",
              game,
            })),
          });

          return {
            tag: {
              id: rowId,
              name: orderRow.order.name,
              slug: orderRow.order.slug,
              description: orderRow.order.description,
              display_order: orderRow.order.display_order,
            },
            games: rowGames,
            layout: readGameSectionLayout(orderRow.order.layout),
            showHeader: readGameSectionShowHeader(orderRow.order.show_header),
            centerWhenSparse: readGameSectionCenterWhenSparse(orderRow.order.center_when_sparse),
            sidePadding: readGameSectionSidePadding(orderRow.order.side_padding),
            curveDirection: readGameSectionCurveDirection(orderRow.order.curve_direction),
            enlargeMiddle: readGameSectionEnlargeMiddle(orderRow.order.enlarge_middle),
            showcaseItems,
            _orderId: orderId,
          };
        })
        .filter((row) => row.games.length > 0 || row.showcaseItems.length > 0);
    }

    return [];
  }, [contentOrders, isMobile]);

  if (gameRows.length === 0) {
    return (
      <div className="bg-white/10 p-8 rounded-2xl text-center">
        <h2 className="text-xl font-bold mb-2">{emptyMessage}</h2>
        <p>{emptyDescription}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {gameRows.map((row) => (
        <GameSectionView
          key={row._orderId ? `content-order-${row._orderId}` : row.tag.id}
          row={row}
          emptyMessage={emptyMessage}
          emptyDescription={emptyDescription}
          pageOrigin={pageOrigin}
        />
      ))}
    </div>
  );
}
